import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [planId, setPlanId] = useState("");
  const [creditsBought, setCreditsBought] = useState(0);
  const [kind, setKind] = useState("plan");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setKind(data.kind || "plan");
          setPlanId(data.plan_id || "");
          setCreditsBought(data.credits || 0);
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts.current >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 8) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="payment-success-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        {status === "checking" && (
          <>
            <div className="h-10 w-10 mx-auto rounded-full border-2 border-brand-gold border-t-transparent animate-spin" />
            <p className="mt-6 font-serif text-2xl text-brand-ink">Confirming your payment...</p>
            <p className="text-sm text-brand-inkLight mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-brand-gold" strokeWidth={1.5} />
            <p className="overline mt-4">Payment confirmed</p>
            {kind === "credits" ? (
              <>
                <h1 className="font-serif text-4xl text-brand-ink mt-3">{creditsBought} interest credits added</h1>
                <p className="text-brand-inkLight mt-3">Your credit balance has been topped up. Each credit unlocks one interest send. Credits never expire.</p>
              </>
            ) : (
              <>
                <h1 className="font-serif text-4xl text-brand-ink mt-3">Welcome to {planId === "vip" ? "VIP Matchmaking" : "Premium"}</h1>
                <p className="text-brand-inkLight mt-3">Your membership is now active. Get ready for a richer matchmaking experience.</p>
              </>
            )}
            <Link to="/dashboard" className="btn-primary inline-block mt-8" data-testid="success-go-dashboard">Go to dashboard</Link>
            <p className="text-[11px] text-brand-inkMute mt-6 max-w-md mx-auto leading-relaxed">
              Charged in the currency shown on your invoice. If this differs from your card&apos;s home currency, your bank may apply its own conversion rate and a small foreign-exchange fee.
            </p>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error" || status === "missing") && (
          <>
            <AlertCircle className="h-14 w-14 mx-auto text-brand-maroon" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-brand-ink mt-4">We couldn't confirm your payment</h1>
            <p className="text-brand-inkLight mt-3 text-sm">If you were charged, your plan will activate within a few minutes. Please refresh or contact support.</p>
            <Link to="/plans" className="btn-ghost inline-block mt-6">Back to plans</Link>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
